import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, AlertTriangle, Coins } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';


export function EBCurrencyBackfillPanel() {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await api.get('/admin/diagnostics/eb-currency-backfill');
      setPreview(data);
      if (data.total === 0) {
        toast.success('No accounts missing currency');
      } else {
        toast.info(`${data.derivable} derivable, ${data.underivable} underivable`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    setApplyResult(null);
    try {
      const { data } = await api.post('/admin/diagnostics/eb-currency-backfill');
      setApplyResult(data);
      setPreview(null);
      if (data.written > 0) {
        toast.success(`Backfilled ${data.written} account(s)`);
      } else {
        toast.info('No changes (0 written)');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Coins className="h-5 w-5" />
          EB Currency Backfill
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Backfill missing currency on EB accounts created before currency persistence was added.
          Derives from IBAN country code (FR&rarr;EUR, GB&rarr;GBP). Preview first, then apply.
        </p>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={runPreview} disabled={loading || applying}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            Preview
          </Button>
          {preview && preview.derivable > 0 && (
            <Button size="sm" variant="default" onClick={runApply} disabled={loading || applying}>
              {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Apply ({preview.derivable})
            </Button>
          )}
        </div>

        {/* Preview results */}
        {preview && (
          <div className="border rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-3">
              <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                PREVIEW (read-only)
              </Badge>
              <span className="text-xs text-muted-foreground">
                {preview.total} account(s) missing currency
              </span>
            </div>

            {/* Summary counts */}
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded border p-2 text-center">
                <p className="text-xs text-muted-foreground">Derivable</p>
                <p className="text-lg font-bold tabular-nums">{preview.derivable}</p>
              </div>
              <div className="rounded border p-2 text-center">
                <p className="text-xs text-muted-foreground">Underivable</p>
                <p className="text-lg font-bold tabular-nums">{preview.underivable}</p>
              </div>
            </div>

            {/* Per-account table */}
            {preview.accounts?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1 pr-2 font-medium text-muted-foreground">Account</th>
                      <th className="text-left py-1 pr-2 font-medium text-muted-foreground">Bank</th>
                      <th className="text-left py-1 pr-2 font-medium text-muted-foreground">IBAN</th>
                      <th className="text-left py-1 pr-2 font-medium text-muted-foreground">Proposed</th>
                      <th className="text-left py-1 font-medium text-muted-foreground">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.accounts.map((a, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1 pr-2">{a.account_name}</td>
                        <td className="py-1 pr-2 text-muted-foreground">{a.bank_name}</td>
                        <td className="py-1 pr-2 font-mono text-muted-foreground">
                          {a.iban ? `****${a.iban.slice(-4)}` : '-'}
                        </td>
                        <td className="py-1 pr-2 font-mono font-bold">
                          {a.proposed_currency || (
                            <span className="text-amber-600">underivable</span>
                          )}
                        </td>
                        <td className="py-1 text-muted-foreground">{a.derivation_source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Apply result */}
        {applyResult && (
          <div className="border rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                APPLIED
              </Badge>
              <span className="text-xs text-muted-foreground">
                {applyResult.written} written, {applyResult.skipped} skipped
              </span>
            </div>

            {applyResult.written_accounts?.length > 0 && (
              <div className="space-y-1">
                {applyResult.written_accounts.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span>{a.account_id}</span>
                    <span className="font-mono font-bold">{a.currency}</span>
                  </div>
                ))}
              </div>
            )}

            {applyResult.skipped_accounts?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Skipped (underivable):
                </p>
                {applyResult.skipped_accounts.map((a, i) => (
                  <p key={i} className="text-xs text-muted-foreground ml-4">{a.account_id}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
